import type { AreaSport } from "./graph-types";
import type { PlannerData } from "./data";
import { reviewedCoverageNote } from "./coverage-policy";

export type PlannerConfidenceNoticeKind =
  | "coverage"
  | "lift-status"
  | "bike-uplift"
  | "weather-fallback"
  | "weather-safety"
  | "trail-scoring"
  | "closures";

export type PlannerConfidenceNotice = {
  kind: PlannerConfidenceNoticeKind;
  message: string;
};

export function plannerConfidenceNotices(
  data: PlannerData,
  sport: AreaSport,
): PlannerConfidenceNotice[] {
  const notices: PlannerConfidenceNotice[] = [];
  const liftCount = data.lifts.length;
  const trailCount = data.trails.length;
  const coverageNote = reviewedCoverageNote(data.area.slug, sport);

  if (coverageNote) {
    notices.push({ kind: "coverage", message: coverageNote });
  } else if (data.area.coverageGrade === "C" || data.area.coverageGrade === "D") {
    notices.push({
      kind: "coverage",
      message:
        sport === "mtb"
          ? `Coverage ${data.area.coverageGrade}: the open-map trail data is thin here.`
          : `Coverage ${data.area.coverageGrade}: the open-map run data is thin here.`,
    });
  }

  if (liftCount > 0) {
    const uncertainLiftCount = data.lifts.filter(
      (l) => l.status === "unknown" || l.status === "scheduled",
    ).length;
    if (uncertainLiftCount / liftCount >= 0.3) {
      notices.push({
        kind: "lift-status",
        message:
          sport === "mtb"
            ? "Live uplift status is limited. Routes using unconfirmed lifts are labelled as previews."
            : "Live lift status is limited. Routes using unconfirmed lifts are labelled as previews.",
      });
    }
  }

  if (sport === "mtb" && liftCount > 0) {
    const unknownBikeLiftCount = data.lifts.filter((l) => l.bikeAccessKnown === false).length;
    if (unknownBikeLiftCount / liftCount >= 0.1) {
      notices.push({
        kind: "bike-uplift",
        message: "Some lifts lack confirmed bike carriage, so ride routes have fewer uplift options.",
      });
    }
  }

  if ((data.weatherContextCoverage?.fallbackTrailCount ?? 0) > 0) {
    notices.push({
      kind: "weather-fallback",
      message:
        sport === "mtb"
          ? "Some trail scores use nearby resort weather because one sector is missing hourly data."
          : "Some run scores use nearby resort weather because one sector is missing hourly data.",
    });
  }

  const safetyNote = data.safetyNotes?.[0];
  if (safetyNote) {
    notices.push({
      kind: "weather-safety",
      message: `Safety note: ${safetyNote}. Check the live conditions page before committing.`,
    });
  }

  if (trailCount > 0) {
    const degradedTrailCount = data.trails.filter((t) => t.degraded).length;
    if (degradedTrailCount / trailCount >= 0.25) {
      notices.push({
        kind: "trail-scoring",
        message:
          sport === "mtb"
            ? "Some trail scores use terrain fallback where aspect data is weak."
            : "Some run scores use terrain fallback where aspect data is weak.",
      });
    }
  }

  if (trailCount > 0) {
    const freshTrailStatusCount = data.trailStatusCoverage?.freshTrailCount ?? 0;
    if (freshTrailStatusCount === 0) {
      notices.push({
        kind: "closures",
        message:
          sport === "mtb"
            ? "Live trail closure data is not connected here yet. Check park signs before riding."
            : "Live run closure data is not connected here yet. Check local signs before committing.",
      });
    } else {
      const totalTrailCount = data.trailStatusCoverage?.totalTrailCount ?? trailCount;
      if (totalTrailCount > 0 && freshTrailStatusCount / totalTrailCount < 0.3) {
        notices.push({
          kind: "closures",
          message:
            sport === "mtb"
              ? "Live trail closure coverage is partial here. Check local signs before committing."
              : "Live run closure coverage is partial here. Check local signs before committing.",
        });
      }
    }
  }

  return notices;
}
